import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import { AlertTriangle, ChevronLeft, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { apiClient } from '@/services/api';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { BOOKING_STATUSES } from '@/constants/bookingStatus';
import type { SyncedBookingInfo } from '@/types/api';
import BookingsListView from './BookingsListView';
import BookingsTimelineView from './BookingsTimelineView';
import BookingFormModal, { type BookingPrefill } from './BookingFormModal';

interface BookingsTabProps {
  restaurantId: number;
}

export default function BookingsTab({ restaurantId }: BookingsTabProps) {
  const [viewMode, setViewMode] = useState<'list' | 'timeline'>('list');
  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [timelineDate, setTimelineDate] = useState(today);
  const [statusFilter, setStatusFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [floorId, setFloorId] = useState<number | null>(null);
  const [selectedBookingUuid, setSelectedBookingUuid] = useState<string | null>(null);
  const [modalBooking, setModalBooking] = useState<SyncedBookingInfo | null>(null);
  const [modalInitial, setModalInitial] = useState<BookingPrefill | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);

  const canCreate = useHasPermission(PERM.BOOKING_CREATE);
  const canUpdate = useHasPermission(PERM.BOOKING_UPDATE);

  // Auto-correct an inverted range so the query doesn't silently return empty.
  const effectiveEndDate = endDate < startDate ? startDate : endDate;

  // Timeline operates on a single day and ignores section/status filters
  // (those are surfaced inside the timeline view itself).
  const queryParams = viewMode === 'timeline'
    ? { startDate: timelineDate, endDate: timelineDate }
    : {
        startDate,
        endDate: effectiveEndDate,
        status: statusFilter || undefined,
        section: sectionFilter || undefined,
      };

  const { data: bookings = [], isLoading, isError: bookingsError, refetch } = useQuery({
    queryKey: ['synced-bookings', restaurantId, queryParams],
    queryFn: () => apiClient.getSyncedBookings(restaurantId, queryParams),
  });

  const { data: tables = [], isError: tablesError, refetch: refetchTables } = useQuery({
    queryKey: ['synced-tables', restaurantId],
    queryFn: () => apiClient.getSyncedTables(restaurantId),
  });

  const { data: restaurant, isError: restaurantError, refetch: refetchRestaurant } = useQuery({
    queryKey: ['restaurant', restaurantId],
    queryFn: () => apiClient.getRestaurant(restaurantId),
    enabled: viewMode === 'timeline',
  });

  const { data: sections = [], isError: sectionsError, refetch: refetchSections } = useQuery({
    queryKey: ['booking-sections', restaurantId],
    queryFn: () => apiClient.getBookingSections(restaurantId),
  });

  const { data: schedules = [], isError: schedulesError, refetch: refetchSchedules } = useQuery({
    queryKey: ['booking-schedules', restaurantId],
    queryFn: () => apiClient.getBookingSchedules(restaurantId),
    enabled: viewMode === 'timeline',
  });

  const { data: specialDays = [], isError: specialDaysError, refetch: refetchSpecialDays } = useQuery({
    queryKey: ['special-days', restaurantId],
    queryFn: () => apiClient.getSpecialDays(restaurantId),
    enabled: viewMode === 'timeline',
  });

  const hasQueryError =
    bookingsError || tablesError || restaurantError || sectionsError || schedulesError || specialDaysError;
  const retryAllQueries = () => {
    refetch();
    refetchTables();
    refetchRestaurant();
    refetchSections();
    refetchSchedules();
    refetchSpecialDays();
  };

  // Floors derived from the synced tables (same source/labels as the timeline).
  const floors = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of tables) {
      if (t.floorId != null) m.set(t.floorId, t.floorName ?? `Floor ${t.floorId}`);
    }
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tables]);

  // posTableId → floorId, used to filter bookings by the selected floor.
  const floorByPosTableId = useMemo(() => {
    const m = new Map<number, number>();
    for (const t of tables) {
      if (t.posTableId != null && t.floorId != null) m.set(t.posTableId, t.floorId);
    }
    return m;
  }, [tables]);

  // List bookings restricted to the selected floor (a booking matches when any
  // of its tables is on that floor). No floor selected → show all.
  const listBookings = useMemo(() => {
    if (floorId == null) return bookings;
    return bookings.filter((b) =>
      (b.shopTableIds ?? '')
        .split(',')
        .map((s) => Number(s.trim()))
        .some((id) => Number.isFinite(id) && floorByPosTableId.get(id) === floorId)
    );
  }, [bookings, floorId, floorByPosTableId]);

  const openCreate = () => {
    setModalBooking(null);
    setModalInitial(undefined);
    setModalOpen(true);
  };

  const openCreatePrefilled = (prefill: BookingPrefill) => {
    setModalBooking(null);
    setModalInitial(prefill);
    setModalOpen(true);
  };

  const openEdit = (booking: SyncedBookingInfo) => {
    setModalBooking(booking);
    setModalInitial(undefined);
    setModalOpen(true);
  };

  const handleRefresh = () => {
    refetch();
  };

  const shiftTimelineDate = (days: number) =>
    setTimelineDate(format(addDays(parseISO(timelineDate), days), 'yyyy-MM-dd'));

  return (
    <div className="space-y-6">
      {hasQueryError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Failed to load booking data.
          </span>
          <button onClick={retryAllQueries} className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 font-medium ml-4">
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* Header Controls */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-4">
          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-l-0 border ${
                viewMode === 'timeline'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Timeline
            </button>
          </div>

          {viewMode === 'list' ? (
            <>
              {/* Date range */}
              <div className="flex items-end gap-2">
                <div>
                  <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">
                    Start date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">
                    End date
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Section Filter */}
              <div>
                <label htmlFor="section" className="block text-xs font-medium text-gray-700 mb-1">
                  Section
                </label>
                <select
                  id="section"
                  value={sectionFilter}
                  onChange={(e) => setSectionFilter(e.target.value)}
                  className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All sections</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.name}>
                      {section.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All</option>
                  {BOOKING_STATUSES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Floor Filter */}
              {floors.length > 0 && (
                <div>
                  <label htmlFor="floor" className="block text-xs font-medium text-gray-700 mb-1">
                    Floor
                  </label>
                  <select
                    id="floor"
                    value={floorId ?? ''}
                    onChange={(e) => setFloorId(e.target.value ? Number(e.target.value) : null)}
                    className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  >
                    <option value="">All floors</option>
                    {floors.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          ) : (
            /* Timeline: single day picker with prev/next arrows — sections/statuses are shown in the view itself */
            <div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => shiftTimelineDate(-1)}
                  className="inline-flex items-center justify-center p-2 border border-gray-300 shadow-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  aria-label="Previous day"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <input
                  type="date"
                  id="timelineDate"
                  value={timelineDate}
                  onChange={(e) => setTimelineDate(e.target.value)}
                  className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                <button
                  type="button"
                  onClick={() => shiftTimelineDate(1)}
                  className="inline-flex items-center justify-center p-2 border border-gray-300 shadow-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  aria-label="Next day"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {viewMode === 'list' && canCreate && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1 px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" />
              New booking
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            aria-label="Refresh bookings"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white shadow rounded-lg">
        {viewMode === 'list' ? (
          <BookingsListView
            bookings={listBookings}
            tables={tables}
            isLoading={isLoading}
            restaurantId={restaurantId}
            selectedBookingUuid={selectedBookingUuid}
            onSelect={setSelectedBookingUuid}
            onEdit={canUpdate ? openEdit : undefined}
          />
        ) : (
          <BookingsTimelineView
            bookings={bookings}
            tables={tables}
            sections={sections}
            schedules={schedules}
            specialDays={specialDays}
            isLoading={isLoading}
            restaurantId={restaurantId}
            selectedDate={timelineDate}
            extendedDayHours={restaurant?.extendedDayHours ?? 0}
            canUpdate={canUpdate}
            canCreate={canCreate}
            selectedBookingUuid={selectedBookingUuid}
            onSelectBooking={setSelectedBookingUuid}
            onEditBooking={canUpdate ? openEdit : undefined}
            onCreateBooking={canCreate ? openCreatePrefilled : undefined}
          />
        )}
      </div>

      {modalOpen && (
        <BookingFormModal
          restaurantId={restaurantId}
          booking={modalBooking}
          initial={modalInitial}
          floorId={floorId}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
